<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const templates = ref([]);
const isLoading = ref(false);
const isSyncing = ref(false);
const isCreating = ref(false);
const showCreateModal = ref(false);
const deleteConfirmName = ref(null);
const usageData = ref(null);
const isLoadingUsage = ref(false);
const usagePeriod = ref(30);

const LANGUAGES = [
  { code: 'pt_BR', label: 'Portugues (BR)' },
  { code: 'en', label: 'English' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'es', label: 'Espanol' },
  { code: 'fr', label: 'Francais' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
];

// Meta pricing per message (USD) — Brazil, as of 2024
// https://developers.facebook.com/docs/whatsapp/pricing
const TEMPLATE_COSTS = {
  MARKETING: 0.0625,
  UTILITY: 0.008,
  AUTHENTICATION: 0.0315,
  SERVICE: 0.0,
};

const PERIOD_OPTIONS = [7, 30, 90];

const HEADER_FORMATS = [
  {
    value: 'TEXT',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_FORMAT_TEXT',
  },
  {
    value: 'IMAGE',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_FORMAT_IMAGE',
  },
  {
    value: 'VIDEO',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_FORMAT_VIDEO',
  },
  {
    value: 'DOCUMENT',
    label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_FORMAT_DOCUMENT',
  },
];

const MEDIA_ACCEPT_TYPES = {
  IMAGE: 'JPEG, PNG',
  VIDEO: 'MP4',
  DOCUMENT: 'PDF',
};

const form = reactive({
  name: '',
  category: 'MARKETING',
  language: 'pt_BR',
  headerFormat: 'TEXT',
  header: '',
  headerMediaUrl: '',
  body: '',
  footer: '',
  buttons: [],
});

const formErrors = reactive({
  name: '',
  body: '',
});

const bodyTextarea = ref(null);
const showEmojiPicker = ref(false);

const COMMON_EMOJIS = [
  '😊',
  '👋',
  '🎉',
  '✅',
  '❤️',
  '🔥',
  '⭐',
  '💰',
  '📦',
  '🛒',
  '📱',
  '💬',
  '🎁',
  '👍',
  '🚀',
  '⏰',
  '📣',
  '🏷️',
  '💡',
  '🔔',
  '✨',
  '💪',
  '🙏',
  '📞',
];

const bodyCharCount = computed(() => form.body.length);

const wrapSelection = (before, after) => {
  const textarea = bodyTextarea.value;
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = form.body;
  const selected = text.substring(start, end);

  if (selected) {
    form.body =
      text.substring(0, start) +
      before +
      selected +
      after +
      text.substring(end);
    nextTick(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    });
  } else {
    form.body = text.substring(0, start) + before + after + text.substring(end);
    nextTick(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length);
    });
  }
};

const insertAtCursor = text => {
  const textarea = bodyTextarea.value;
  if (!textarea) return;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  form.body = form.body.substring(0, start) + text + form.body.substring(end);
  nextTick(() => {
    textarea.focus();
    textarea.setSelectionRange(start + text.length, start + text.length);
  });
};

const formatBold = () => wrapSelection('*', '*');
const formatItalic = () => wrapSelection('_', '_');
const formatStrike = () => wrapSelection('~', '~');
const formatCode = () => wrapSelection('```', '```');
const insertNewline = () => insertAtCursor('\n');
const insertEmoji = emoji => {
  insertAtCursor(emoji);
  showEmojiPicker.value = false;
};

const getBodyText = tmpl => {
  const body = tmpl.components?.find(c => c.type === 'BODY');
  return body?.text || '';
};

const getHeaderFormat = tmpl => {
  const header = tmpl.components?.find(c => c.type === 'HEADER');
  return header?.format || null;
};

const HEADER_ICONS = {
  IMAGE: 'i-lucide-image',
  VIDEO: 'i-lucide-video',
  DOCUMENT: 'i-lucide-file-text',
};

const statusConfig = {
  APPROVED: {
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.APPROVED'),
    classes: 'bg-n-teal-2 text-n-teal-11',
  },
  PENDING: {
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.PENDING'),
    classes: 'bg-n-amber-2 text-n-amber-11',
  },
  REJECTED: {
    label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.REJECTED'),
    classes: 'bg-n-ruby-2 text-n-ruby-11',
  },
};

const getStatus = status => {
  return (
    statusConfig[status] || {
      label: status,
      classes: 'bg-n-slate-2 text-n-slate-11',
    }
  );
};

const sortedTemplates = computed(() => {
  const list = Array.isArray(templates.value) ? templates.value : [];
  return [...list].sort((a, b) => {
    const order = { APPROVED: 0, PENDING: 1, REJECTED: 2 };
    return (order[a.status] ?? 3) - (order[b.status] ?? 3);
  });
});

const estimatedCost = computed(() => {
  if (!usageData.value?.per_template) return 0;
  let total = 0;
  usageData.value.per_template.forEach(item => {
    const tmpl = templates.value.find(tp => tp.name === item.name);
    const category = tmpl?.category || 'MARKETING';
    const cost = TEMPLATE_COSTS[category] || TEMPLATE_COSTS.MARKETING;
    total += item.count * cost;
  });
  return total;
});

const businessAccountId = computed(() => {
  return props.inbox.provider_config?.business_account_id || '';
});

const metaBusinessUrl = computed(() => {
  const baid = businessAccountId.value;
  if (baid) {
    return `https://business.facebook.com/wa/manage/message-templates/?waba_id=${baid}`;
  }
  return 'https://business.facebook.com/settings/whatsapp-business-accounts';
});

const metaPaymentUrl = computed(() => {
  return 'https://business.facebook.com/billing_hub/payment_methods';
});

const fetchTemplates = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxesAPI.getTemplates(props.inbox.id);
    templates.value = data.templates || [];
  } catch {
    templates.value = [];
  } finally {
    isLoading.value = false;
  }
};

const fetchUsage = async () => {
  isLoadingUsage.value = true;
  try {
    const { data } = await InboxesAPI.getTemplateUsage(
      props.inbox.id,
      usagePeriod.value
    );
    usageData.value = data;
  } catch {
    usageData.value = null;
  } finally {
    isLoadingUsage.value = false;
  }
};

const syncTemplates = async () => {
  isSyncing.value = true;
  try {
    const { data } = await InboxesAPI.syncTemplatesManual(props.inbox.id);
    templates.value = data.templates || [];
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.SYNCED'));
  } catch {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.SYNC_ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

const validateForm = () => {
  let valid = true;
  formErrors.name = '';
  formErrors.body = '';

  if (!form.name.trim()) {
    formErrors.name = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_ERROR');
    valid = false;
  } else if (!/^[a-z][a-z0-9_]*$/.test(form.name)) {
    formErrors.name = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_FORMAT_ERROR');
    valid = false;
  }

  if (!form.body.trim()) {
    formErrors.body = t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_ERROR');
    valid = false;
  }

  return valid;
};

const resetForm = () => {
  form.name = '';
  form.category = 'MARKETING';
  form.language = 'pt_BR';
  form.headerFormat = 'TEXT';
  form.header = '';
  form.headerMediaUrl = '';
  form.body = '';
  form.footer = '';
  form.buttons = [];
  formErrors.name = '';
  formErrors.body = '';
};

const buildComponents = () => {
  const components = [];

  if (form.headerFormat === 'TEXT') {
    if (form.header.trim()) {
      components.push({ type: 'HEADER', format: 'TEXT', text: form.header });
    }
  } else if (form.headerMediaUrl.trim()) {
    components.push({
      type: 'HEADER',
      format: form.headerFormat,
      example: { header_url: [form.headerMediaUrl.trim()] },
    });
  }

  components.push({ type: 'BODY', text: form.body });
  if (form.footer.trim()) {
    components.push({ type: 'FOOTER', text: form.footer });
  }
  if (form.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(b => ({
        type: b.type,
        text: b.text,
        ...(b.type === 'URL' ? { url: b.url } : {}),
      })),
    });
  }
  return components;
};

const handleCreate = async () => {
  if (!validateForm()) return;

  isCreating.value = true;
  try {
    await InboxesAPI.createTemplate(props.inbox.id, {
      name: form.name,
      category: form.category,
      language: form.language,
      components: buildComponents(),
    });
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.CREATED'));
    showCreateModal.value = false;
    resetForm();
    await fetchTemplates();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.CREATE_ERROR');
    useAlert(msg);
  } finally {
    isCreating.value = false;
  }
};

const handleDelete = async templateName => {
  try {
    await InboxesAPI.deleteTemplate(props.inbox.id, templateName);
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.DELETED'));
    deleteConfirmName.value = null;
    await fetchTemplates();
  } catch {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.ALERTS.DELETE_ERROR'));
  }
};

const addButton = () => {
  if (form.buttons.length >= 3) return;
  form.buttons.push({ type: 'QUICK_REPLY', text: '', url: '' });
};

const removeButton = index => {
  form.buttons.splice(index, 1);
};

const openCreateModal = () => {
  resetForm();
  showCreateModal.value = true;
};

const formatCurrency = value => {
  return `$${value.toFixed(2)}`;
};

const costPerMessage = category => {
  const cost = TEMPLATE_COSTS[category] || TEMPLATE_COSTS.MARKETING;
  return `~${formatCurrency(cost)}/${t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.PER_MSG')}`;
};

onMounted(() => {
  fetchTemplates();
  fetchUsage();
});
</script>

<template>
  <div class="mx-8 flex flex-col gap-6">
    <!-- Meta Quick Links Banner -->
    <div
      class="flex flex-wrap items-center gap-3 p-4 rounded-xl border border-n-blue-6 bg-n-blue-2"
    >
      <span class="i-lucide-external-link size-4 text-n-blue-11 shrink-0" />
      <span class="text-sm text-n-blue-11 font-medium">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINKS.LABEL') }}
      </span>
      <div class="flex items-center gap-2 ml-auto">
        <a
          :href="metaBusinessUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 transition-colors"
        >
          <span class="i-lucide-layout-template size-3.5" />
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINKS.MANAGE_TEMPLATES') }}
        </a>
        <a
          :href="metaPaymentUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-n-blue-3 text-n-blue-11 hover:bg-n-blue-4 transition-colors"
        >
          <span class="i-lucide-credit-card size-3.5" />
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINKS.PAYMENT_METHODS') }}
        </a>
      </div>
    </div>

    <!-- Templates Section -->
    <SettingsSectionCard
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION')"
    >
      <template #header-actions>
        <div class="flex items-center gap-2">
          <Button
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC')"
            icon="i-lucide-refresh-cw"
            slate
            faded
            xs
            :is-loading="isSyncing"
            @click="syncTemplates"
          />
          <Button
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW')"
            icon="i-lucide-plus"
            blue
            xs
            @click="openCreateModal"
          />
        </div>
      </template>

      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
      </div>

      <!-- Empty state -->
      <div
        v-else-if="!sortedTemplates.length"
        class="flex flex-col items-center justify-center gap-4 py-12"
      >
        <span class="i-lucide-message-square-plus size-10 text-n-slate-8" />
        <div class="flex flex-col items-center gap-1.5 max-w-sm text-center">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_STATE.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY_STATE.DESCRIPTION') }}
          </span>
        </div>
        <div class="flex items-center gap-2 mt-2">
          <Button
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.NEW')"
            icon="i-lucide-plus"
            blue
            xs
            @click="openCreateModal"
          />
          <Button
            :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC')"
            icon="i-lucide-refresh-cw"
            slate
            faded
            xs
            :is-loading="isSyncing"
            @click="syncTemplates"
          />
        </div>
      </div>

      <!-- Template list -->
      <div v-else class="flex flex-col gap-2">
        <div
          v-for="tmpl in sortedTemplates"
          :key="tmpl.name + tmpl.language"
          class="flex items-start justify-between gap-4 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
        >
          <div class="flex flex-col gap-1.5 min-w-0 flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-sm font-semibold text-n-slate-12 font-mono">
                {{ tmpl.name }}
              </span>
              <span
                class="px-1.5 py-0.5 text-[11px] font-medium rounded"
                :class="getStatus(tmpl.status).classes"
              >
                {{ getStatus(tmpl.status).label }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <span>{{ tmpl.category }}</span>
              <span>·</span>
              <span>{{ tmpl.language }}</span>
              <template v-if="HEADER_ICONS[getHeaderFormat(tmpl)]">
                <span>·</span>
                <span class="inline-flex items-center gap-1">
                  <span
                    :class="HEADER_ICONS[getHeaderFormat(tmpl)]"
                    class="size-3"
                  />
                  {{ getHeaderFormat(tmpl) }}
                </span>
              </template>
              <span>·</span>
              <span>{{ costPerMessage(tmpl.category) }}</span>
            </div>
            <p
              v-if="getBodyText(tmpl)"
              class="text-sm text-n-slate-11 line-clamp-2 mt-0.5"
            >
              {{ getBodyText(tmpl) }}
            </p>
          </div>

          <div class="flex items-center gap-1 shrink-0">
            <button
              v-if="deleteConfirmName === tmpl.name"
              class="px-2 py-1 text-xs font-medium rounded text-n-ruby-11 bg-n-ruby-2 hover:bg-n-ruby-3 cursor-pointer transition-colors"
              @click="handleDelete(tmpl.name)"
            >
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.CONFIRM_DELETE') }}
            </button>
            <button
              class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-2 cursor-pointer transition-colors"
              :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE')"
              @click="
                deleteConfirmName =
                  deleteConfirmName === tmpl.name ? null : tmpl.name
              "
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </SettingsSectionCard>

    <!-- Pricing Reference (only show when templates exist) -->
    <SettingsSectionCard
      v-if="sortedTemplates.length"
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.TITLE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.DESCRIPTION')"
    >
      <div class="overflow-hidden rounded-lg border border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-solid-3">
              <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.CATEGORY') }}
              </th>
              <th class="px-4 py-2.5 text-right font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.PRICE_USD') }}
              </th>
              <th class="px-4 py-2.5 text-left font-medium text-n-slate-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.NOTES') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_PRICE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_NOTE') }}
              </td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_PRICE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_NOTE') }}
              </td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.AUTH_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.AUTH_PRICE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.AUTH_NOTE') }}
              </td>
            </tr>
            <tr class="border-t border-n-weak">
              <td class="px-4 py-2.5 text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.SERVICE_LABEL') }}
              </td>
              <td class="px-4 py-2.5 text-right font-mono text-n-teal-11">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.FREE') }}
              </td>
              <td class="px-4 py-2.5 text-n-slate-10">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.SERVICE_NOTE') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mt-2 text-xs text-n-slate-9">
        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.DISCLAIMER') }}
      </p>
    </SettingsSectionCard>

    <!-- Usage Stats (only show when templates exist) -->
    <SettingsSectionCard
      v-if="sortedTemplates.length"
      :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.TITLE')"
      :description="t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.DESCRIPTION')"
    >
      <template #header-actions>
        <select
          v-model="usagePeriod"
          class="px-2 py-1 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 cursor-pointer"
          @change="fetchUsage"
        >
          <option v-for="days in PERIOD_OPTIONS" :key="days" :value="days">
            {{ days }} {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.DAYS') }}
          </option>
        </select>
      </template>

      <div v-if="isLoadingUsage" class="flex items-center justify-center py-8">
        <span class="i-lucide-loader-2 size-4 animate-spin text-n-slate-10" />
      </div>

      <div v-else-if="usageData" class="flex flex-col gap-4">
        <!-- Summary cards -->
        <div class="grid grid-cols-2 gap-3">
          <div
            class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.TOTAL_SENT') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ usageData.total_sent }}
            </span>
          </div>
          <div
            class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
          >
            <span class="text-xs text-n-slate-10">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.ESTIMATED_COST') }}
            </span>
            <span class="text-2xl font-semibold text-n-slate-12">
              {{ formatCurrency(estimatedCost) }}
            </span>
          </div>
        </div>

        <!-- Per-template breakdown -->
        <div
          v-if="usageData.per_template?.length"
          class="overflow-hidden rounded-lg border border-n-weak"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="bg-n-solid-3">
                <th class="px-4 py-2 text-left font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.TEMPLATE') }}
                </th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.SENDS') }}
                </th>
                <th class="px-4 py-2 text-right font-medium text-n-slate-11">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.COST') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in usageData.per_template"
                :key="item.name"
                class="border-t border-n-weak"
              >
                <td class="px-4 py-2 font-mono text-n-slate-12">
                  {{ item.name }}
                </td>
                <td class="px-4 py-2 text-right text-n-slate-12">
                  {{ item.count }}
                </td>
                <td class="px-4 py-2 text-right font-mono text-n-slate-12">
                  {{
                    formatCurrency(
                      item.count *
                        (TEMPLATE_COSTS[
                          templates.find(tp => tp.name === item.name)?.category
                        ] || 0.0625)
                    )
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="py-6 text-center text-sm text-n-slate-10">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.USAGE.NO_SENDS') }}
        </div>
      </div>
    </SettingsSectionCard>

    <!-- Create Template Modal -->
    <teleport to="body">
      <div
        v-if="showCreateModal"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
        @click.self="showCreateModal = false"
      >
        <div
          class="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-n-solid-2 border border-n-weak shadow-2xl"
        >
          <div
            class="flex items-center justify-between p-5 border-b border-n-weak"
          >
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.TITLE') }}
            </h3>
            <button
              class="p-1 rounded-lg text-n-slate-10 hover:bg-n-solid-3 cursor-pointer transition-colors"
              @click="showCreateModal = false"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <div class="flex flex-col gap-4 p-5">
            <!-- Name -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_LABEL') }}
              </label>
              <input
                v-model="form.name"
                type="text"
                class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand font-mono"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_PLACEHOLDER')
                "
                @input="
                  form.name = form.name.toLowerCase().replace(/[^a-z0-9_]/g, '')
                "
              />
              <span class="text-xs text-n-slate-9">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.NAME_HINT') }}
              </span>
              <span v-if="formErrors.name" class="text-xs text-n-ruby-11">
                {{ formErrors.name }}
              </span>
            </div>

            <!-- Category -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CATEGORY_LABEL') }}
              </label>
              <div class="flex items-center gap-4">
                <label
                  class="flex items-center gap-2 cursor-pointer"
                  @click="form.category = 'MARKETING'"
                >
                  <span
                    class="size-4 rounded-full border-2 grid place-content-center transition-colors"
                    :class="
                      form.category === 'MARKETING'
                        ? 'border-n-brand bg-n-brand'
                        : 'border-n-slate-8'
                    "
                  >
                    <span
                      v-if="form.category === 'MARKETING'"
                      class="size-1.5 rounded-full bg-white"
                    />
                  </span>
                  <span class="text-sm text-n-slate-12">
                    {{
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.MARKETING_LABEL')
                    }}
                  </span>
                </label>
                <label
                  class="flex items-center gap-2 cursor-pointer"
                  @click="form.category = 'UTILITY'"
                >
                  <span
                    class="size-4 rounded-full border-2 grid place-content-center transition-colors"
                    :class="
                      form.category === 'UTILITY'
                        ? 'border-n-brand bg-n-brand'
                        : 'border-n-slate-8'
                    "
                  >
                    <span
                      v-if="form.category === 'UTILITY'"
                      class="size-1.5 rounded-full bg-white"
                    />
                  </span>
                  <span class="text-sm text-n-slate-12">
                    {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.COST.UTILITY_LABEL') }}
                  </span>
                </label>
              </div>
            </div>

            <!-- Language -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.LANGUAGE_LABEL') }}
              </label>
              <select
                v-model="form.language"
                class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand cursor-pointer"
              >
                <option
                  v-for="lang in LANGUAGES"
                  :key="lang.code"
                  :value="lang.code"
                >
                  {{ lang.label }}
                </option>
              </select>
            </div>

            <!-- Header (optional) -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_LABEL') }}
              </label>
              <select
                v-model="form.headerFormat"
                class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand cursor-pointer"
              >
                <option
                  v-for="fmt in HEADER_FORMATS"
                  :key="fmt.value"
                  :value="fmt.value"
                >
                  {{ t(fmt.label) }}
                </option>
              </select>

              <!-- Text header -->
              <input
                v-if="form.headerFormat === 'TEXT'"
                v-model="form.header"
                type="text"
                class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_PLACEHOLDER')
                "
              />

              <!-- Media header URL -->
              <template v-else>
                <input
                  v-model="form.headerMediaUrl"
                  type="url"
                  class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                  :placeholder="
                    t(
                      'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_MEDIA_PLACEHOLDER'
                    )
                  "
                />
                <div
                  class="flex items-start gap-2 p-3 rounded-lg bg-n-amber-2 border border-n-amber-6"
                >
                  <span
                    class="i-lucide-info size-4 text-n-amber-11 shrink-0 mt-0.5"
                  />
                  <div class="flex flex-col gap-1 text-xs text-n-amber-11">
                    <span>
                      {{
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_MEDIA_HINT',
                          { formats: MEDIA_ACCEPT_TYPES[form.headerFormat] }
                        )
                      }}
                    </span>
                    <span>
                      {{
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.HEADER_MEDIA_SEND_HINT'
                        )
                      }}
                    </span>
                  </div>
                </div>
              </template>
            </div>

            <!-- Body -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_LABEL') }}
                <span class="text-n-ruby-11">*</span>
              </label>

              <!-- Formatting Toolbar -->
              <div
                class="flex items-center gap-0.5 p-1 border border-b-0 rounded-t-lg bg-n-solid-3 border-n-weak"
              >
                <button
                  type="button"
                  class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                  :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_BOLD')"
                  @click="formatBold"
                >
                  <span class="i-lucide-bold size-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                  :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_ITALIC')"
                  @click="formatItalic"
                >
                  <span class="i-lucide-italic size-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                  :title="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_STRIKETHROUGH')
                  "
                  @click="formatStrike"
                >
                  <span class="i-lucide-strikethrough size-4" />
                </button>
                <button
                  type="button"
                  class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                  :title="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_CODE')"
                  @click="formatCode"
                >
                  <span class="i-lucide-code size-4" />
                </button>

                <span class="w-px h-4 bg-n-weak mx-0.5" />

                <button
                  type="button"
                  class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                  :title="
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_NEWLINE')
                  "
                  @click="insertNewline"
                >
                  <span class="i-lucide-wrap-text size-4" />
                </button>

                <!-- Emoji picker toggle -->
                <div class="relative">
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-solid-4 hover:text-n-slate-12 cursor-pointer transition-colors"
                    :title="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_EMOJI')
                    "
                    @click="showEmojiPicker = !showEmojiPicker"
                  >
                    <span class="i-lucide-smile size-4" />
                  </button>
                  <div
                    v-if="showEmojiPicker"
                    class="absolute top-full left-0 mt-1 z-10 p-2 rounded-lg bg-n-solid-2 border border-n-weak shadow-lg grid grid-cols-8 gap-1 w-[220px]"
                  >
                    <button
                      v-for="emoji in COMMON_EMOJIS"
                      :key="emoji"
                      type="button"
                      class="p-1 rounded hover:bg-n-solid-3 text-base cursor-pointer transition-colors text-center leading-none"
                      @click="insertEmoji(emoji)"
                    >
                      {{ emoji }}
                    </button>
                  </div>
                </div>

                <span class="ml-auto text-xs text-n-slate-9 pr-1">
                  {{
                    t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_CHARS', {
                      count: bodyCharCount,
                    })
                  }}
                </span>
              </div>

              <textarea
                ref="bodyTextarea"
                v-model="form.body"
                rows="5"
                maxlength="1024"
                class="px-3 py-2 text-sm border rounded-b-lg rounded-t-none bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand resize-none font-mono"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_PLACEHOLDER')
                "
                @focus="showEmojiPicker = false"
              />
              <span class="text-xs text-n-slate-9">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FORMAT_HINT') }}
              </span>
              <span class="text-xs text-n-slate-9">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BODY_HINT') }}
              </span>
              <span v-if="formErrors.body" class="text-xs text-n-ruby-11">
                {{ formErrors.body }}
              </span>
            </div>

            <!-- Footer (optional) -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER_LABEL') }}
              </label>
              <input
                v-model="form.footer"
                type="text"
                maxlength="60"
                class="px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand"
                :placeholder="
                  t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.FOOTER_PLACEHOLDER')
                "
              />
            </div>

            <!-- Buttons -->
            <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTONS_LABEL') }}
                </label>
                <button
                  v-if="form.buttons.length < 3"
                  class="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg text-n-brand hover:bg-n-brand-2 cursor-pointer transition-colors"
                  @click="addButton"
                >
                  <span class="i-lucide-plus size-3" />
                  {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.ADD_BUTTON') }}
                </button>
              </div>

              <div
                v-for="(btn, idx) in form.buttons"
                :key="idx"
                class="flex items-start gap-2 p-3 border rounded-lg bg-n-solid-1 border-n-weak"
              >
                <div class="flex flex-col gap-2 flex-1">
                  <div class="flex items-center gap-2">
                    <select
                      v-model="btn.type"
                      class="px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 cursor-pointer"
                    >
                      <option value="QUICK_REPLY">
                        {{
                          t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.QUICK_REPLY')
                        }}
                      </option>
                      <option value="URL">
                        {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_TYPE') }}
                      </option>
                    </select>
                    <input
                      v-model="btn.text"
                      type="text"
                      class="flex-1 px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                      :placeholder="
                        t(
                          'INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.BUTTON_TEXT_PLACEHOLDER'
                        )
                      "
                    />
                  </div>
                  <input
                    v-if="btn.type === 'URL'"
                    v-model="btn.url"
                    type="text"
                    class="px-2 py-1.5 text-xs border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 placeholder:text-n-slate-8"
                    :placeholder="
                      t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.URL_PLACEHOLDER')
                    "
                  />
                </div>
                <button
                  class="p-1 rounded text-n-slate-10 hover:text-n-ruby-11 cursor-pointer transition-colors"
                  @click="removeButton(idx)"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- Modal Actions -->
          <div
            class="flex items-center justify-end gap-3 p-5 border-t border-n-weak"
          >
            <Button
              :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.CANCEL')"
              slate
              faded
              @click="showCreateModal = false"
            />
            <Button
              :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.SUBMIT')"
              icon="i-lucide-send"
              blue
              :is-loading="isCreating"
              @click="handleCreate"
            />
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
